/**
 * Compact sparkline (Patch E25).
 *
 * Inline SVG with no chart-library dependency. Renders a line through
 * the data points with optional dots and a highlighted final point.
 *
 * The visual contract:
 *   - Bars (not lines) when the dataset is tiny (≤ 4) — lines look silly
 *     with 2 points.
 *   - Lines for ≥ 5 points, with a dot at every point so individual months
 *     are still readable.
 *   - The last point gets a filled accent dot — that's "now", the manager's
 *     anchor.
 *   - All-zero series renders a flat baseline at the bottom (not a horizontal
 *     line in the middle, which would lie about the scale).
 *
 * Sizes through props; uses currentColor for the stroke so the parent can
 * tint it via Tailwind text-* classes. The accent dot uses the existing
 * --color-accent token.
 */

import { cn } from '@/lib/cn';

interface Props {
  values: number[];
  width?: number;
  height?: number;
  className?: string;
  /** Render each datum as a bar instead of a polyline. */
  forceBars?: boolean;
}

export function Sparkline({
  values,
  width = 80,
  height = 24,
  className,
  forceBars,
}: Props) {
  if (values.length === 0) {
    return (
      <svg
        width={width}
        height={height}
        className={cn('text-tertiary', className)}
        aria-hidden
      />
    );
  }

  const max = Math.max(...values, 1); // guard divide-by-zero
  const useBars = forceBars ?? values.length <= 4;
  const padding = 2;
  const innerW = width - padding * 2;
  const innerH = height - padding * 2;

  if (useBars) {
    const barWidth = innerW / values.length;
    return (
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        className={cn('text-secondary', className)}
        role="img"
        aria-label={`Trend: ${values.join(', ')}`}
      >
        {values.map((v, i) => {
          const isLast = i === values.length - 1;
          const h = v === 0 ? 1 : (v / max) * innerH;
          const x = padding + i * barWidth + barWidth * 0.15;
          const y = padding + innerH - h;
          return (
            <rect
              key={i}
              x={x}
              y={y}
              width={barWidth * 0.7}
              height={h}
              rx={1}
              fill={isLast ? 'var(--color-accent)' : 'currentColor'}
              opacity={isLast ? 1 : 0.6}
            />
          );
        })}
      </svg>
    );
  }

  // Line + dots
  const stepX = values.length === 1 ? 0 : innerW / (values.length - 1);
  const points = values.map((v, i) => {
    const x = padding + i * stepX;
    const y = padding + innerH - (v / max) * innerH;
    return { x, y, v };
  });
  const pathD = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`)
    .join(' ');

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className={cn('text-secondary', className)}
      role="img"
      aria-label={`Trend: ${values.join(', ')}`}
    >
      <path
        d={pathD}
        fill="none"
        stroke="currentColor"
        strokeWidth={1.25}
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity={0.7}
      />
      {points.map((p, i) => {
        const isLast = i === points.length - 1;
        return (
          <circle
            key={i}
            cx={p.x}
            cy={p.y}
            r={isLast ? 2.2 : 1.5}
            fill={isLast ? 'var(--color-accent)' : 'currentColor'}
            opacity={isLast ? 1 : 0.6}
          />
        );
      })}
    </svg>
  );
}
